/**
 * header.js — <site-header> custom element.
 * Sticky "is-scrolled" state, mobile menu toggle, outside-click and Escape handling.
 * Closing the menu returns focus to the toggle button.
 */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.site-header');
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.menu = this.querySelector('.mobile-menu');
    this.openLabel = this.toggle ? this.toggle.getAttribute('data-open-label') : '';
    this.closeLabel = this.toggle ? this.toggle.getAttribute('data-close-label') : '';

    this.onScroll = () => {
      if (this.header) this.header.classList.toggle('is-scrolled', window.scrollY > 20);
    };
    this.onToggle = () => this.setOpen(!this.menu.classList.contains('is-open'));
    this.onDocumentClick = (event) => {
      if (this.menu && this.toggle && this.menu.classList.contains('is-open') &&
          !this.menu.contains(event.target) && !this.toggle.contains(event.target)) {
        this.setOpen(false);
      }
    };
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.menu && this.menu.classList.contains('is-open')) {
        this.setOpen(false);
        if (this.toggle) this.toggle.focus();
      }
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
    this.onScroll();
  }

  setOpen(isOpen) {
    if (!this.menu || !this.toggle) return;
    this.menu.classList.toggle('is-open', isOpen);
    this.toggle.setAttribute('aria-expanded', String(isOpen));
    if (this.openLabel && this.closeLabel) {
      this.toggle.setAttribute('aria-label', isOpen ? this.closeLabel : this.openLabel);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
  }
}

if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

/**
 * <announcement-bar> — optional dismissible bar in the header group (hidden by default).
 */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.key = 'announcement-dismissed-' + (this.getAttribute('data-section-id') || '');
    try {
      if (window.sessionStorage.getItem(this.key) === '1') this.hidden = true;
    } catch (e) { /* storage unavailable */ }
    this.closeButton = this.querySelector('[data-dismiss]');
    this.onDismiss = () => {
      this.hidden = true;
      try { window.sessionStorage.setItem(this.key, '1'); } catch (e) { /* ignore */ }
    };
    if (this.closeButton) this.closeButton.addEventListener('click', this.onDismiss);
  }

  disconnectedCallback() {
    if (this.closeButton && this.onDismiss) this.closeButton.removeEventListener('click', this.onDismiss);
  }
}

if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
